import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n, LANGUAGES } from "./i18n";
import { updateProfile } from "./accountClient";
import { toast } from "react-toastify";

const fadeIn  = keyframes`from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Trigger button ──────────────────────────────────────────────────────── */
const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: .4rem;
  height: 32px;
  padding: .32rem .7rem;
  border-radius: 7px;
  border: 1px solid ${({ $open, theme }) => $open ? theme.borderLight : theme.border};
  background: ${({ $open, theme }) => $open ? theme.bgHover : "transparent"};
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: .75rem;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
  &:hover {
    border-color: ${({ theme }) => theme.borderLight};
    color: ${({ theme }) => theme.text};
  }
`;

const Flag  = styled.span`font-size: .9rem; line-height: 1;`;
const Arrow = styled.span`
  font-size: .55rem;
  color: ${({ theme }) => theme.text};
  transition: transform .2s;
  transform: ${({ $open }) => $open ? "rotate(180deg)" : "rotate(0)"};
`;

/* ─── Dropdown ─────────────────────────────────────────────────────────────── */
const DropWrap = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 200;
  background: ${({ theme }) => theme.bgCard};
  border: 1px solid ${({ theme }) => theme.borderLight};
  border-radius: 10px;
  padding: .35rem;
  min-width: 160px;
  box-shadow: 0 8px 32px rgba(0,0,0,.4);
  animation: ${fadeIn} .15s ease;
`;

const LangItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: .6rem;
  padding: .5rem .7rem;
  border-radius: 7px;
  border: none;
  background: ${({ $active, theme }) => $active ? theme.bgHover : "transparent"};
  color: ${({ $active, theme }) => $active ? theme.text : theme.textDim};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: .78rem;
  cursor: pointer;
  text-align: left;
  transition: all .12s;
  &:hover { background: ${({ theme }) => theme.bgHover}; color: ${({ theme }) => theme.text}; }
`;

const Check = styled.span`
  margin-left: auto;
  font-size: .7rem;
  color: ${({ theme }) => theme.green};
`;

const Relative = styled.div`position: relative;
  @media (max-width: 780px) {
      display: ${({user}) => user?"none":"block"};
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────────── */
export default function LanguageSwitcher({ compact = false, user }) {
  const { lang, setLang, currentLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref  = useRef(null);
  const [saving,    setSaving]    = useState(false);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (code) => {
    save(code);
    setLang(code);
    setOpen(false);
  
  };

    const save = async (code) => {
      setSaving(true);
      try {
        await updateProfile({ language:code, });

      } catch(err) {

        } finally { setSaving(false); }
    };

  return (
    <Relative user={user} ref={ref}>
      <Trigger $open={open} onClick={() => setOpen(o => !o)}>
        <Flag>{currentLang.flag}</Flag>
        {!compact && <span>{currentLang.label}</span>}
        <Arrow $open={open}>▼</Arrow>
      </Trigger>

      {open && (
        <DropWrap>
          {LANGUAGES.map(l => (
            <LangItem key={l.code} $active={lang === l.code} onClick={() => handleSelect(l.code)}>
              <span style={{ fontSize: ".9rem" }}>{l.flag}</span>
              <span>{l.label}</span>
              {lang === l.code && <Check>✓</Check>}
            </LangItem>
          ))}
        </DropWrap>
      )}
    </Relative>
  );
}
